import moment from "moment";
import type {
  CoverageInformation,
  DeductibleAmountEntry,
  MiscellaneousAmountEntry,
  OutOfPocketAmountEntry,
  PatientResponsibilityEntry,
  PlanInformation,
  ProviderInformation,
  SingleCoverageInformation,
} from "../../../data-model/coverageInformation";
import {
  CoverageResult,
  Demographics,
  PatientResponsibility,
  Payload,
  SingleCoverage,
} from "../../../data-model/medicalPaOrdersModel";
import {
  CoverageMiscellaneousAmountType,
  CoveragePlanDisplayStatus,
  CoverageSubscriberType,
} from "../../../enums/coverageInformationEnums";
import { mapCoverageNetworkTypeToDisplay } from "../../../enums/coverageNetworkTypes";
import { CoverageStatus } from "../../../enums/coverageStatus";
import { Submission } from "../../../hooks/singleMedicalPaOrderDetails/useSubmissionInformation";
import { formatDateToUTCWithFormat } from "../../../utils/formatDate";
import {
  getGroupedCoverageTabsForDisplay,
  type GroupedCoverageTab,
} from "./coverageDisplayOrder";
import {
  determineStatusCodeForCoverage,
  mapCoverageStatus,
} from "./coverageStatusUtils";
import { buildAdditionalPayerCoverageInformation } from "./mapAdditionalPayerCoverageInformation";
import { buildMedicaidCoverageInformation } from "./mapMedicaidCoverageInformation";
import {
  compareCoverageServiceTypeCodes,
  resolveCoverageServiceTypeCode,
} from "./resolveCoverageServiceType";

const cleanAmountData = (amount: string) => amount.replace(/[^0-9.-]+/g, "");

/**
 * Parses a raw payer-supplied amount/percentage string into a number.
 *
 * Treats `undefined`, `null`, empty string, and the explicit `"-"`
 * sentinel (both pre- and post-cleanup) as "no value" and returns
 * `undefined` so callers can distinguish missing data from a real `0`.
 *
 * @param value - Raw string from the upstream payer payload
 * @returns Parsed number, or `undefined` when the value is missing/invalid
 */
const parseOptionalNumber = (value: string | undefined): number | undefined => {
  if (value === undefined || value === null || value === "" || value === "-")
    return undefined;
  const cleaned = cleanAmountData(String(value));
  if (cleaned === "" || cleaned === "-") return undefined;
  const n = parseFloat(cleaned);
  return Number.isNaN(n) ? undefined : n;
};

/**
 * Preserves the payer amount string for display (null/undefined become undefined).
 *
 * @param value - Raw field from patient responsibility JSON
 * @returns Trimmed string when present, otherwise undefined
 */
const preserveRawAmount = (
  value: string | undefined | null,
): string | undefined => {
  if (value === undefined || value === null) {
    return undefined;
  }
  const trimmed = String(value).trim();
  return trimmed === "" ? undefined : trimmed;
};

/**
 * Stores the raw payer miscellaneous amount string without reformatting.
 *
 * @param value - `co_pay` or `co_insurance` from the API
 * @returns Value for {@link MiscellaneousAmountEntry.amount}
 */
const toMiscStoredAmount = (value: string | undefined | null): string =>
  preserveRawAmount(value) ?? "";

const toPlanDisplayStatus = (
  coverage: SingleCoverage,
): CoveragePlanDisplayStatus => {
  const code = determineStatusCodeForCoverage(coverage?.coverage_status ?? "");
  switch (code) {
    case "active":
      return CoveragePlanDisplayStatus.ACTIVE;
    case "inactive":
      return CoveragePlanDisplayStatus.INACTIVE;
    default:
      return CoveragePlanDisplayStatus.UNKNOWN;
  }
};

/**
 * Extracts a Medicaid `plan_date` fallback for the plan's effective date.
 *
 * Medicaid coverage rows frequently omit `service_year_from`. When they do, the
 * Plan Summary falls back to the Medicaid processing metadata, checking
 * `medicaid_eligibility_details.plan_date` first and then
 * `medicaid_nycsc_info.plan_date` (e.g. "7/1/2026"). The value is normalized to
 * the `YYYY-MM-DD` storage format used for the other plan dates so downstream
 * display formatting stays consistent.
 *
 * @param coverageResult - The coverage_result row for the active tier
 * @returns Normalized `YYYY-MM-DD` date, or `""` when missing or unparseable
 */
const getMedicaidEffectiveDateFallback = (
  coverageResult?: CoverageResult,
): string => {
  const processingMetadata = coverageResult?.processing_metadata as
    Record<string, unknown> | undefined;
  const eligibilityDetails =
    processingMetadata?.medicaid_eligibility_details as
      Record<string, unknown> | undefined;
  const nycscInfo = processingMetadata?.medicaid_nycsc_info as
    Record<string, unknown> | undefined;
  const planDate = eligibilityDetails?.plan_date ?? nycscInfo?.plan_date;
  if (
    planDate === undefined ||
    planDate === null ||
    String(planDate).trim() === ""
  ) {
    return "";
  }
  const parsed = moment(String(planDate), [
    "M/D/YYYY",
    "MM/DD/YYYY",
    "YYYY-MM-DD",
    moment.ISO_8601,
  ]);
  return parsed.isValid() ? parsed.format("YYYY-MM-DD") : "";
};

const buildPlanInformation = (
  coverage: SingleCoverage,
  pr: PatientResponsibility | undefined,
  coverageResult?: CoverageResult,
): PlanInformation => {
  const effectiveDate =
    formatDateToUTCWithFormat(
      coverage?.service_year_from ?? "",
      "YYYY-MM-DD",
      "",
    ) || getMedicaidEffectiveDateFallback(coverageResult);
  return {
    planName: coverage?.plan_name ?? "",
    memberId: coverage?.member_id ?? "",
    effectiveDate,
    planEndDate: formatDateToUTCWithFormat(
      coverage?.service_year_to ?? pr?.service_year_to ?? "",
      "YYYY-MM-DD",
      "",
    ),
    planStatus: toPlanDisplayStatus(coverage),
    authManagingEntity: pr?.auth_managing_entity?.trim() || undefined,
  };
};

const buildDeductibleEntries = (
  pr: PatientResponsibility | undefined,
): DeductibleAmountEntry[] => {
  const networkType = mapCoverageNetworkTypeToDisplay(
    pr?.deductibles_network_type,
  );
  const individual: DeductibleAmountEntry = {
    subscriberType: CoverageSubscriberType.INDIVIDUAL,
    totalAmount: parseOptionalNumber(pr?.individual_deductibles),
    totalAmountRaw: preserveRawAmount(pr?.individual_deductibles),
    usedAmount: parseOptionalNumber(pr?.individual_deductibles_met),
    usedAmountRaw: preserveRawAmount(pr?.individual_deductibles_met),
    networkType,
  };
  const family: DeductibleAmountEntry = {
    subscriberType: CoverageSubscriberType.FAMILY,
    totalAmount: parseOptionalNumber(pr?.family_deductibles),
    totalAmountRaw: preserveRawAmount(pr?.family_deductibles),
    usedAmount: parseOptionalNumber(pr?.family_deductibles_met),
    usedAmountRaw: preserveRawAmount(pr?.family_deductibles_met),
    networkType,
  };
  return [individual, family];
};

const buildOutOfPocketEntries = (
  pr: PatientResponsibility | undefined,
): OutOfPocketAmountEntry[] => {
  const networkType = mapCoverageNetworkTypeToDisplay(
    pr?.out_of_pocket_network_type,
  );
  return [
    {
      subscriberType: CoverageSubscriberType.INDIVIDUAL,
      totalAmount: parseOptionalNumber(pr?.individual_out_of_pocket),
      totalAmountRaw: preserveRawAmount(pr?.individual_out_of_pocket),
      usedAmount: parseOptionalNumber(pr?.individual_out_of_pocket_met),
      usedAmountRaw: preserveRawAmount(pr?.individual_out_of_pocket_met),
      networkType,
    },
    {
      subscriberType: CoverageSubscriberType.FAMILY,
      totalAmount: parseOptionalNumber(pr?.family_out_of_pocket),
      totalAmountRaw: preserveRawAmount(pr?.family_out_of_pocket),
      usedAmount: parseOptionalNumber(pr?.family_out_of_pocket_met),
      usedAmountRaw: preserveRawAmount(pr?.family_out_of_pocket_met),
      networkType,
    },
  ];
};

const buildMiscellaneousEntries = (
  pr: PatientResponsibility | undefined,
): MiscellaneousAmountEntry[] => {
  return [
    {
      type: CoverageMiscellaneousAmountType.CO_INSURANCE,
      amount: toMiscStoredAmount(pr?.co_insurance),
    },
    {
      type: CoverageMiscellaneousAmountType.CO_PAY,
      amount: toMiscStoredAmount(pr?.co_pay),
    },
  ];
};

/**
 * Builds one patient-responsibility tab entry for a single coverage row.
 *
 * @param coverage - Coverage row containing patient responsibility amounts
 * @param coverageResult - Optional matching coverage-result row
 * @param payload - Order payload containing service type description fallback
 * @returns One treatment-level tab entry for the selected coverage row
 */
const buildPatientResponsibilityEntryForRow = (
  coverage: SingleCoverage,
  coverageResult: CoverageResult | undefined,
  payload: Payload | undefined,
  allowPayloadFallback: boolean,
): PatientResponsibilityEntry => {
  const pr = coverage?.patient_responsibility;
  const resultPr = coverageResult?.patient_responsibility;
  const nameFromCoverageResult =
    resultPr &&
    typeof resultPr === "object" &&
    typeof (resultPr as Record<string, unknown>).service_type_name === "string"
      ? String(
          (resultPr as Record<string, unknown>).service_type_name ?? "",
        ).trim()
      : "";
  const serviceTypeDescription =
    nameFromCoverageResult || payload?.service_type_name || "Treatment";
  const serviceType = resolveCoverageServiceTypeCode(
    coverage,
    coverageResult,
    payload,
    allowPayloadFallback,
  );

  return {
    singlePatientResponsibility: {
      serviceTypeInformation: {
        serviceType,
        serviceTypeDescription,
      },
      deductibleAmountInformation: buildDeductibleEntries(pr),
      outOfPocketAmountInformation: buildOutOfPocketEntries(pr),
      miscellaneousAmountInformation: buildMiscellaneousEntries(pr),
    },
  };
};

/**
 * Builds patient-responsibility entries for one grouped plan tab.
 *
 * @param rows - All rows that belong to the same plan tab
 * @param payload - Order payload containing service type description fallback
 * @returns Treatment-level tab entries shown under one insurance tab
 */
const buildPatientResponsibilityEntriesForCoverageRows = (
  rows: Array<{
    coverage: SingleCoverage;
    coverageResult?: CoverageResult;
  }>,
  payload?: Payload,
): PatientResponsibilityEntry[] => {
  if (!rows.length) {
    return [];
  }
  const allowPayloadFallback = rows.length === 1;
  const entries = rows.map((row) =>
    buildPatientResponsibilityEntryForRow(
      row.coverage,
      row.coverageResult,
      payload,
      allowPayloadFallback,
    ),
  );
  return entries.sort((left, right) =>
    compareCoverageServiceTypeCodes(
      left.singlePatientResponsibility.serviceTypeInformation.serviceType,
      right.singlePatientResponsibility.serviceTypeInformation.serviceType,
    ),
  );
};

/**
 * Builds the provider information block for a coverage tab.
 *
 * Provider name and NPI are sourced from the order's `submissionData`
 * (`requesting_practitioner_details`) which holds the canonical, pre-parsed
 * first/last/NPI values — this avoids the previous fragile approach of
 * splitting the rotated `payload.practitioner_name` string by spaces, which
 * misbehaved for single-token names, suffixes, and middle initials.
 *
 * Provider Tax ID precedence (first non-empty wins):
 *   1. `taxId` — the facility-level Tax ID supplied by the caller
 *      (sourced from the `singleHealthFacilityDetails` Redux slice in
 *      `useCoverageData`). Trimmed before use.
 *   2. `coverageRequest.providerTaxId` — the value originally captured on
 *      the coverage request, kept as a fallback for older orders whose
 *      facility record does not yet expose a Tax ID.
 *   3. Empty string.
 *
 * @param coverageRequest - Raw `coverage_request` JSON for this coverage row
 *   (used as the fallback source for `providerTaxId`)
 * @param submissionData - Order submission details containing the requesting
 *   practitioner's first name, last name, and NPI
 * @param taxId - Optional facility-level Tax ID; when present and non-empty
 *   after trimming, overrides the coverage-request fallback
 * @returns ProviderInformation populated with first name, last name, NPI, and tax ID
 */
const buildProviderInformationFromCoverageRequest = (
  coverageRequest: Record<string, unknown> | undefined,
  submissionData: Submission | undefined,
  payload?: Payload,
  taxId?: string,
): ProviderInformation => {
  const firstName =
    submissionData?.requesting_practitioner_details?.first_name?.trim()
      ? submissionData?.requesting_practitioner_details?.first_name?.trim()
      : getProviderFirstNameAndLastNameFromCommaSeperatedString(
          payload?.practitioner_name as string,
        ).firstName;
  const lastName =
    submissionData?.requesting_practitioner_details?.last_name?.trim()
      ? submissionData?.requesting_practitioner_details?.last_name?.trim()
      : getProviderFirstNameAndLastNameFromCommaSeperatedString(
          payload?.practitioner_name as string,
        ).lastName;

  return {
    providerFirstName: firstName,
    providerLastName: lastName,
    providerNPI: submissionData?.requesting_practitioner_details?.npi ?? "",
    providerTaxID:
      taxId?.trim() ?? (coverageRequest?.["providerTaxId"] as string) ?? "",
  };
};

const buildOrderInformationFromPostgres = (
  payload?: Payload,
  coverageRequest?: Record<string, unknown>,
) => {
  const dosFromPayload = payload?.date_of_service;
  const dosFromRequest = coverageRequest?.["dateOfService"] as
    string | undefined;
  const dos = dosFromPayload ?? dosFromRequest;
  return {
    dateOfService: formatDateToUTCWithFormat(dos ?? "", "YYYY-MM-DD"),
  };
};

const buildPatientDemographicsFromPostgres = (demographics?: Demographics) => {
  return {
    dateOfBirth: formatDateToUTCWithFormat(
      demographics?.date_of_birth ?? "",
      "YYYY-MM-DD",
      "",
    ),
    gender: demographics?.sex ?? "",
  };
};

/**
 * Maps a single grouped coverage tab into the per-tab coverage view-model.
 *
 * Threads the facility-level `taxId` through to
 * `buildProviderInformationFromCoverageRequest` so the Provider
 * Information block prefers the facility's Tax ID over the value captured
 * on the coverage request.
 *
 * @param groupedCoverageTab - One coverage tab (representative coverage row
 *   plus the underlying rows that aggregate into its patient-responsibility
 *   list)
 * @param demographics - Patient demographics from the order payload
 * @param payload - Order payload (date of service, service type)
 * @param submissionData - Order submission details (practitioner name + NPI)
 * @param taxId - Optional facility-level Tax ID; forwarded to the provider
 *   information builder where it takes precedence over the coverage
 *   request's `providerTaxId`
 * @returns A single tab entry for the `CoverageInformation` array
 */
const mapSingleCoverageFromPostgres = (
  groupedCoverageTab: GroupedCoverageTab,
  demographics: Demographics | undefined,
  payload: Payload | undefined,
  submissionData: Submission | undefined,
  taxId?: string,
) => {
  const coverage = groupedCoverageTab.representativeCoverage;
  const coverageResult = groupedCoverageTab.representativeCoverageResult;
  const pr = coverage?.patient_responsibility;
  const req = coverageResult?.coverage_request;

  return {
    singleCoverageInformation: {
      planInformation: buildPlanInformation(coverage, pr, coverageResult),
      providerInformation: buildProviderInformationFromCoverageRequest(
        req,
        submissionData,
        payload,
        taxId,
      ),
      orderInformation: buildOrderInformationFromPostgres(payload, req),
      patientDemographics: buildPatientDemographicsFromPostgres(demographics),
      patientResponsibilityInformation:
        buildPatientResponsibilityEntriesForCoverageRows(
          groupedCoverageTab.rows,
          payload,
        ),
      payerInformation: coverage?.payer_name ?? "",
      coverageStatus: mapCoverageStatus(coverage?.status ?? ""),
      medicaidInformation: buildMedicaidCoverageInformation(
        coverage?.payer_name,
        coverageResult,
      ),
      additionalPayerInformation:
        buildAdditionalPayerCoverageInformation(coverageResult),
    },
  };
};

/**
 * Maps Postgres `coverage` and `coverage_results` rows into the redesigned coverage
 * display model (one tab per unique plan in primary → secondary → tertiary order).
 * Rows for the same plan are aggregated into treatment-level tabs.
 *
 * @param coverages - Coverage rows from the coverage table
 * @param coverageResults - Matching coverage_result rows
 * @param demographics - Patient demographics from the general order payload
 * @param payload - Order payload for service type and date of service
 * @param submissionData - Order submission details; supplies the requesting
 *   practitioner's first name, last name, and NPI for the Provider
 *   Information block on each coverage tab
 * @param taxId - Optional facility-level Tax ID (from the
 *   `singleHealthFacilityDetails` Redux slice). When non-empty, takes
 *   precedence over `providerTaxId` from the coverage request when
 *   populating each tab's Provider Information.
 * @returns CoverageInformation entries for the tabbed coverage UI
 */
export const mapPostgresCoveragesToCoverageInformation = (
  coverages: SingleCoverage[],
  coverageResults: CoverageResult[],
  demographics?: Demographics,
  payload?: Payload,
  submissionData?: Submission | undefined,
  taxId?: string,
): CoverageInformation => {
  if (!coverages?.length) return [];

  const groupedCoverageTabs = getGroupedCoverageTabsForDisplay(
    coverages,
    coverageResults,
  );

  return groupedCoverageTabs.map((groupedCoverageTab) =>
    mapSingleCoverageFromPostgres(
      groupedCoverageTab,
      demographics,
      payload,
      submissionData,
      taxId,
    ),
  );
};

/**
 * Splits a `"LastName, FirstName"` string into its `lastName` and `firstName`
 * parts. Used as a fallback for {@link buildProviderInformationFromCoverageRequest}
 * when the canonical submission record is missing the practitioner's parsed
 * first / last name.
 *
 * Expected input format: a single comma separating last name (before) from
 * first name (after); both sides are trimmed.
 *
 * @param name - Practitioner name in `"Last, First"` format. When undefined or
 *   empty, both returned fields are empty strings.
 * @returns Object with `lastName` and `firstName` (both trimmed).
 */
const getProviderFirstNameAndLastNameFromCommaSeperatedString = (
  name?: string,
) => {
  if (!name) return { lastName: "", firstName: "" };
  const [lastName, firstName] = name.split(",");
  return {
    lastName: lastName?.trim(),
    firstName: firstName?.trim(),
  };
};

/**
 * Builds a minimal {@link SingleCoverageInformation} from order-level fields when
 * Postgres coverage rows are absent. Used for demographics sidebar + empty-state
 * layouts on the coverage screen.
 *
 * @param demographics - Patient demographics from the order payload
 * @param payload - Order payload (date of service, etc.)
 * @param submissionData - Requesting practitioner details for the provider block
 * @param taxId - Optional facility-level Tax ID for the provider block
 * @returns Snapshot with demographics/provider/order context and no plan or BV data
 */
export const buildOrderContextOnlyCoverageSnapshot = (
  demographics?: Demographics,
  payload?: Payload,
  submissionData?: Submission | undefined,
  taxId?: string,
): SingleCoverageInformation => {
  return {
    planInformation: {
      planName: "",
      memberId: "",
      effectiveDate: "",
      planEndDate: "",
      planStatus: CoveragePlanDisplayStatus.UNKNOWN,
    },
    providerInformation: buildProviderInformationFromCoverageRequest(
      undefined,
      submissionData,
      undefined,
      taxId,
    ),
    orderInformation: buildOrderInformationFromPostgres(payload, undefined),
    patientDemographics: buildPatientDemographicsFromPostgres(demographics),
    patientResponsibilityInformation: [],
    payerInformation: "",
    coverageStatus: CoverageStatus.UNKNOWN,
  };
};
